import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  catchError,
  filter,
  first,
  map,
  Observable,
  switchMap,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import {
  DrawingFile,
  DrawingFileStatus,
} from '../../models/drawing-file.model';

@Injectable({ providedIn: 'root' })
export class DrawingFileService {
  private http = inject(HttpClient);

  postDrawingFile$(
    file: File,
    options?: {
      quoteId?: number;
      quoteItemPersistentId?: string;
    },
  ): Observable<DrawingFile> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    if (options?.quoteId != null && options?.quoteItemPersistentId) {
      formData.append('quote_id', String(options.quoteId));
      formData.append(
        'quote_item_persistent_id',
        options.quoteItemPersistentId,
      );
    }

    return this.http.post<DrawingFile>('/quotes-api/drawing-files/', formData);
  }

  getDrawingFile(id: number): Observable<DrawingFile> {
    return this.http.get<DrawingFile>(`/quotes-api/drawing-file/${id}/`);
  }

  confirmDrawingFile$(
    drawingFileId: number,
    payload: {
      length: number;
      width: number;
      depth: number;
      units: string;
      part_number?: string;
      title?: string;
      revision?: string;
      material?: string;
      holes?: number | null;
      profile_edge_length?: number;
      hole_edge_length?: number;
      tolerances?: string;
      general_notes?: string;
      complexity?: string | null;
    },
  ): Observable<DrawingFile> {
    return this.http.post<DrawingFile>(
      `/quotes-api/drawing-file/${drawingFileId}/confirm/`,
      payload,
    );
  }

  /**
   * Poll until drawing status is completed or failed.
   * Mirrors CadFileService.pollCadFileUntilComplete.
   */
  pollDrawingFileUntilTerminal(
    drawingFileId: number,
    pollIntervalMs: number = 2000,
    timeoutMs: number = 300000,
    onProgress?: (drawingFile: DrawingFile) => void,
  ): Observable<DrawingFile> {
    return timer(0, pollIntervalMs).pipe(
      switchMap(() => this.getDrawingFile(drawingFileId)),
      map((drawingFile) => {
        onProgress?.(drawingFile);
        return drawingFile;
      }),
      filter((drawingFile) => this.isTerminalStatus(drawingFile.status)),
      first(),
      timeout(timeoutMs),
      catchError((error) => {
        if (error.name === 'TimeoutError') {
          return throwError(
            () =>
              new Error(
                `Drawing extraction timed out after ${timeoutMs / 1000} seconds`,
              ),
          );
        }
        return throwError(() => error);
      }),
    );
  }

  private isTerminalStatus(status: DrawingFileStatus): boolean {
    return status === 'completed' || status === 'failed';
  }
}
